// Attempts to unify various date representations into a comparable timestamp.
function parseDateValue(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  if (!str) return null;
  const firstPass = Date.parse(str.replace(/\s+/g, ' '));
  if (!Number.isNaN(firstPass)) return firstPass;
  const match = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!match) return null;
  const [, day, month, year, hour = '0', minute = '0', second = '0'] = match;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  ).getTime();
}

function compareValues(a, b, key, dir) {
  const mul = dir === 'desc' ? -1 : 1;
  const va = a?.[key];
  const vb = b?.[key];
  if (key === 'datum') {
    const ta = parseDateValue(va);
    const tb = parseDateValue(vb);
    if (ta !== null && tb !== null) return (ta - tb) * mul;
    if (ta !== null) return 1 * mul;
    if (tb !== null) return -1 * mul;
    const sa = typeof va === 'string' ? va : (va ? String(va) : '');
    const sb = typeof vb === 'string' ? vb : (vb ? String(vb) : '');
    if (sa === sb) return 0;
    return sa < sb ? -1 * mul : 1 * mul;
  }
  if (va === vb) return 0;
  if (va === undefined || va === null) return -1 * mul;
  if (vb === undefined || vb === null) return 1 * mul;
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
  const sa = String(va);
  const sb = String(vb);
  return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' }) * mul;
}

function createPagination(totalItems, page, pageSize, onChange) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const wrapper = document.createElement('nav');
  wrapper.className = 'cases-table-pagination';

  const mkBtn = (label, disabled, targetPage) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    btn.disabled = !!disabled;
    btn.addEventListener('click', () => onChange(targetPage));
    return btn;
  };

  wrapper.appendChild(mkBtn('Předchozí', page <= 1, Math.max(1, page - 1)));

  const windowSize = 7;
  let start = Math.max(1, page - Math.floor(windowSize / 2));
  let end = Math.min(totalPages, start + windowSize - 1);
  if (end - start + 1 < windowSize) start = Math.max(1, end - windowSize + 1);

  for (let p = start; p <= end; p++) {
    const btn = mkBtn(String(p), p === page, p);
    if (p === page) btn.setAttribute('aria-current', 'page');
    wrapper.appendChild(btn);
  }

  wrapper.appendChild(mkBtn('Další', page >= totalPages, Math.min(totalPages, page + 1)));
  return wrapper;
}

const DEFAULT_ROW_ID_KEYS = ['id', 'cisloRizeni', 'cislo_rizeni'];

function defaultResolveRowId(row) {
  if (!row) return null;
  for (const key of DEFAULT_ROW_ID_KEYS) {
    if (row[key] !== undefined && row[key] !== null) {
      return String(row[key]);
    }
  }
  return null;
}

function toHtmlString(content) {
  if (content === null || content === undefined) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'number' || typeof content === 'boolean') return String(content);
  if (typeof Node !== 'undefined' && content instanceof Node) {
    const wrapper = document.createElement('div');
    wrapper.appendChild(content.cloneNode(true));
    return wrapper.innerHTML;
  }
  return String(content);
}

export function renderCasesTable(container, data, options = {}) {
  const columns = options.columns || [];
  const state = {
    sortKey: options.initialSortKey || 'datum',
    sortDir: options.initialSortDir || 'desc',
    page: 1,
    pageSize: options.pageSize || 10,
    expandedRowId: new Set(),
    loadingRowId: new Set(),
    loadingStartedAt: new Map(),
    loadTicket: new Map()
  };

  const onDetail = options.onDetail;
  const detailLinkColumnKey = options.detailLinkColumnKey;
  const actionsColumnKey = options.actionsColumnKey || '__actions';
  const nonSortableKeys = new Set(options.nonSortableKeys || []);
  const onCaseLink = typeof options.onCaseLink === 'function' ? options.onCaseLink : null;
  const getRowActions = typeof options.getRowActions === 'function' ? options.getRowActions : null;
  const onAction = typeof options.onAction === 'function' ? options.onAction : null;
  const loadRowDetail = typeof options.rowDetailLoader === 'function' ? options.rowDetailLoader : null;
  const rowDetailMinLoadMs = Number.isFinite(options.rowDetailMinLoadMs) ? Number(options.rowDetailMinLoadMs) : 0;
  const resolveRowId = typeof options.resolveRowId === 'function' ? options.resolveRowId : defaultResolveRowId;
  const onRowToggle = typeof options.onRowToggle === 'function' ? options.onRowToggle : null;
  const onRender = typeof options.onRender === 'function' ? options.onRender : null;
  const onDetailRender = typeof options.onDetailRender === 'function' ? options.onDetailRender : null;
  const allowRowExpansion = !!loadRowDetail;
  const detailCache = new Map();
  const dropdownRefs = new Set();
  const detailLoadingMessage = options.rowDetailLoadingMessage || 'Načítání detailu řízení...';
  const detailLoadingMarkup = options.rowDetailLoadingMarkup || `
    <div class="cases-table__detail-loading">
      <span class="spinner"></span>
      <span>${detailLoadingMessage}</span>
    </div>`;

  const closeDropdown = (dropdown) => {
    if (!dropdown) return;
    try {
      if (typeof dropdown.close === 'function') {
        dropdown.close();
      } else {
        dropdown.removeAttribute('open');
      }
    } catch {
      dropdown.removeAttribute('open');
    }
  };

  const closeOtherDropdowns = (current) => {
    dropdownRefs.forEach(dropdown => {
      if (dropdown !== current) {
        closeDropdown(dropdown);
      }
    });
  };

  const renderLinkCell = (td, row, value) => {
    const resolvedValue = value === null || value === undefined ? '' : String(value);
    const span = document.createElement('span');
    span.className = 'cases-table__case-id';
    span.textContent = resolvedValue;
    const caseId = resolveRowId(row);
    if (onCaseLink && caseId) {
      span.tabIndex = 0;
      span.setAttribute('role', 'button');
      span.setAttribute('aria-label', 'Zobrazit detail řízení');
      const handleActivate = (event) => {
        if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onCaseLink(row, span, event);
      };
      span.addEventListener('click', handleActivate);
      span.addEventListener('keydown', handleActivate);
    }
    td.appendChild(span);
  };

  const renderActionsCell = (td, row, rowId) => {
    td.classList.add('cases-table__menu-cell', 'gov-table--fit-content', 'gov-table--border-left');
    const actions = getRowActions ? getRowActions(row) : [];
    const wrapper = document.createElement('div');
    wrapper.className = 'cases-table__menu-actions';
    if (!actions.length) {
      if (typeof onDetail === 'function') {
        const btn = document.createElement('gov-button');
        btn.setAttribute('variant', 'primary');
        btn.setAttribute('type', 'solid');
        btn.setAttribute('size', 's');
        btn.setAttribute('wcag-label', 'Zobrazit detail řízení');
        btn.textContent = 'Detail';
        btn.addEventListener('gov-click', () => onDetail(row, btn));
        wrapper.appendChild(btn);
      }
      // Don't render anything (no placeholder) when there are no actions and no onDetail
    } else {
      const dropdown = document.createElement('gov-dropdown');
      dropdown.setAttribute('position', 'right');
      dropdownRefs.add(dropdown);

      const trigger = document.createElement('gov-button');
      trigger.setAttribute('variant', 'neutral');
      trigger.setAttribute('type', 'base');
      trigger.setAttribute('size', 's');
      trigger.setAttribute('icon-left', 'basic/three-dots-vertical');
      trigger.setAttribute('wcag-label', 'Další akce');
      trigger.addEventListener('gov-click', () => closeOtherDropdowns(dropdown));
      dropdown.appendChild(trigger);

      const list = document.createElement('ul');
      list.setAttribute('slot', 'list');
      list.setAttribute('role', 'menu');

      actions.forEach((action) => {
        const li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        const actionBtn = document.createElement('gov-button');
        actionBtn.setAttribute('variant', 'primary');
        actionBtn.setAttribute('type', 'base');
        actionBtn.setAttribute('size', 'm');
        actionBtn.setAttribute('expanded', 'true');
        actionBtn.setAttribute('role', 'menuitem');
        if (action.icon) actionBtn.setAttribute('icon-left', action.icon);
        actionBtn.textContent = action.label;
        if (action.disabled) {
          actionBtn.setAttribute('disabled', 'true');
        } else if (onAction) {
          actionBtn.addEventListener('gov-click', () => {
            closeDropdown(dropdown);
            closeOtherDropdowns(dropdown);
            onAction(action, row);
          });
        }
        li.appendChild(actionBtn);
        list.appendChild(li);
      });

      dropdown.appendChild(list);
      wrapper.appendChild(dropdown);
    }

    if (allowRowExpansion) {
      const toggleBtn = document.createElement('gov-button');
      toggleBtn.setAttribute('variant', 'neutral');
      toggleBtn.setAttribute('type', 'base');
      toggleBtn.setAttribute('size', 's');
      toggleBtn.setAttribute('icon-left', state.expandedRowId.has(rowId) ? 'basic/chevron-up' : 'basic/chevron-down');
      toggleBtn.setAttribute('wcag-label', state.expandedRowId.has(rowId) ? 'Skrýt detail řízení' : 'Zobrazit detail řízení');
      toggleBtn.setAttribute('title', 'Detail');
      toggleBtn.addEventListener('gov-click', () => toggleRowExpansion(row, rowId));
      wrapper.appendChild(toggleBtn);
    }

    td.appendChild(wrapper);
  };

  const setExpandedState = (row, rowId, expanded, { forceReload = false } = {}) => {
    if (!allowRowExpansion || !rowId) return false;
    
    const wasExpanded = state.expandedRowId.has(rowId);
    
    if (expanded) {
      if (forceReload) detailCache.delete(rowId);
      state.expandedRowId.add(rowId);
      if (!detailCache.has(rowId)) {
        startRowDetailLoad(row, rowId);
      } else {
        state.loadingRowId.delete(rowId);
      }
    } else if (wasExpanded) {
      state.expandedRowId.delete(rowId);
      state.loadingRowId.delete(rowId);
    }
    
    if (onRowToggle) onRowToggle(rowId, row, expanded);
    
    // Efficiently update just this row instead of full re-render
    if (wasExpanded !== expanded) {
      updateRowExpansion(row, rowId, expanded);
    }
    
    return true;
  };

  const updateRowExpansion = (row, rowId, expanded) => {
    if (!rowId) return;
    
    // Find the main row element
    const mainRow = container.querySelector(`tr[data-row-id="${rowId}"]`);
    if (!mainRow) {
      // Row not found, might be on different page - do full render
      render();
      return;
    }
    
    // Update toggle button icon
    const toggleBtn = mainRow.querySelector('gov-button[wcag-label*="detail"]');
    if (toggleBtn) {
      toggleBtn.setAttribute('icon-left', expanded ? 'basic/chevron-up' : 'basic/chevron-down');
      toggleBtn.setAttribute('wcag-label', expanded ? 'Skrýt detail řízení' : 'Zobrazit detail řízení');
      toggleBtn.setAttribute('title', 'Detail');
    }
    
    if (expanded) {
      // Check if detail row already exists
      let detailRow = mainRow.nextElementSibling;
      if (!detailRow || !detailRow.classList.contains('cases-table__detail-row')) {
        // Create new detail row
        detailRow = document.createElement('tr');
        detailRow.className = 'cases-table__detail-row';
        const detailTd = document.createElement('td');
        detailTd.colSpan = columns.length;
        detailTd.className = 'cases-table__detail-cell';
        detailRow.appendChild(detailTd);
        mainRow.parentNode.insertBefore(detailRow, mainRow.nextSibling);
      }
      
      // Update detail content
      const detailTd = detailRow.querySelector('td');
      const isLoading = state.loadingRowId.has(rowId);
      if (isLoading) {
        detailTd.innerHTML = detailLoadingMarkup;
      } else {
        detailTd.innerHTML = detailCache.get(rowId) || '<div class="cases-table__detail-empty">Detail není k dispozici.</div>';
      }
      
      if (onDetailRender) {
        try {
          onDetailRender({ rowId, row, cell: detailTd, isLoading });
        } catch {
          /* no-op */
        }
      }
    } else {
      // Remove detail row if it exists
      const detailRow = mainRow.nextElementSibling;
      if (detailRow && detailRow.classList.contains('cases-table__detail-row')) {
        detailRow.remove();
      }
    }
  };

  const toggleRowExpansion = (row, rowId) => {
    if (!rowId) return;
    const shouldExpand = !state.expandedRowId.has(rowId);
    setExpandedState(row, rowId, shouldExpand);
  };

  const startRowDetailLoad = (row, rowId) => {
    if (!loadRowDetail || !rowId) return;
    const startedAt = Date.now();
    state.loadingRowId.add(rowId);
    state.loadingStartedAt.set(rowId, startedAt);
    const currentTicket = (state.loadTicket.get(rowId) || 0) + 1;
    state.loadTicket.set(rowId, currentTicket);
    Promise.resolve()
      .then(() => loadRowDetail(row, rowId))
      .then(async (content) => {
        const normalized = toHtmlString(content) || '';
        const elapsed = Date.now() - startedAt;
        if (rowDetailMinLoadMs > 0 && elapsed < rowDetailMinLoadMs) {
          await new Promise((resolve) => setTimeout(resolve, rowDetailMinLoadMs - elapsed));
        }
        detailCache.set(rowId, normalized);
      })
      .catch(() => {
        detailCache.set(rowId, '<div class="cases-table__detail-error">Detail se nepodařilo načíst.</div>');
      })
      .finally(() => {
        if (state.loadTicket.get(rowId) === currentTicket) {
          state.loadingRowId.delete(rowId);
          state.loadingStartedAt.delete(rowId);
          // Update only this row instead of full re-render
          if (state.expandedRowId.has(rowId)) {
            updateRowExpansion(row, rowId, true);
          }
        }
      });
  };

  const render = () => {
    container.innerHTML = '';
    dropdownRefs.clear();

    const table = document.createElement('table');
    table.className = 'cases-table';

    const thead = document.createElement('thead');
    const trHead = document.createElement('tr');
    columns.forEach(col => {
      const th = document.createElement('th');
      th.textContent = col.label;
      if (col.hideMobile) th.classList.add('hide-for-mobile');
      const isSortable = !nonSortableKeys.has(col.key) && col.key !== actionsColumnKey;
      if (isSortable) {
        th.style.cursor = 'pointer';
        th.setAttribute('role', 'button');
        th.addEventListener('click', () => {
          if (state.sortKey === col.key) {
            state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc';
          } else {
            state.sortKey = col.key;
            state.sortDir = 'asc';
          }
          state.page = 1;
          render();
        });
        th.setAttribute('aria-sort', state.sortKey === col.key ? (state.sortDir === 'asc' ? 'ascending' : 'descending') : 'none');
      } else {
        th.setAttribute('aria-sort', 'none');
      }
      trHead.appendChild(th);
    });
    thead.appendChild(trHead);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    const sorted = [...data].sort((a, b) => compareValues(a, b, state.sortKey, state.sortDir));
    const startIdx = (state.page - 1) * state.pageSize;
    const pageItems = sorted.slice(startIdx, startIdx + state.pageSize);

    if (pageItems.length === 0) {
      const tr = document.createElement('tr');
      const td = document.createElement('td');
      td.colSpan = columns.length;
      td.textContent = 'Žádná data';
      tr.appendChild(td);
      tbody.appendChild(tr);
    } else {
      pageItems.forEach(row => {
        const tr = document.createElement('tr');
        const rowId = resolveRowId(row);
        if (rowId) {
          tr.setAttribute('data-row-id', rowId);
        }
        columns.forEach(col => {
          const td = document.createElement('td');
          if (col.hideMobile) td.classList.add('hide-for-mobile');
          if (col.key === actionsColumnKey) {
            renderActionsCell(td, row, rowId);
          } else {
            const value = row[col.key] ?? '';
            if (col.key === detailLinkColumnKey) {
              renderLinkCell(td, row, value);
            } else {
              td.textContent = value;
            }
          }
          tr.appendChild(td);
        });
        tbody.appendChild(tr);

        if (allowRowExpansion && rowId && state.expandedRowId.has(rowId)) {
          const detailTr = document.createElement('tr');
          detailTr.className = 'cases-table__detail-row';
          const detailTd = document.createElement('td');
          detailTd.colSpan = columns.length;
          detailTd.className = 'cases-table__detail-cell';
          const isLoading = state.loadingRowId.has(rowId);
          if (isLoading) {
            detailTd.innerHTML = detailLoadingMarkup;
          } else {
            detailTd.innerHTML = detailCache.get(rowId) || '<div class="cases-table__detail-empty">Detail není k dispozici.</div>';
          }
          if (onDetailRender) {
            try {
              onDetailRender({ rowId, row, cell: detailTd, isLoading });
            } catch {
              /* no-op */
            }
          }
          detailTr.appendChild(detailTd);
          tbody.appendChild(detailTr);
        }
      });
    }

    table.appendChild(tbody);
    container.appendChild(table);

    const pager = createPagination(data.length, state.page, state.pageSize, (newPage) => {
      state.page = newPage;
      render();
    });
    container.appendChild(pager);

    if (onRender) {
      try {
        onRender({ state: { ...state } });
      } catch {
        /* no-op */
      }
    }
  };

  render();

  const findRowById = (rowId) => {
    if (!rowId) return { row: null, index: -1 };
    const index = data.findIndex((row) => resolveRowId(row) === rowId);
    if (index === -1) return { row: null, index: -1 };
    return { row: data[index], index };
  };

  return {
    expandRowById(rowId, { forceReload = false } = {}) {
      const { row, index } = findRowById(rowId);
      if (!row) return false;
      const targetPage = Math.floor(index / state.pageSize) + 1;
      if (state.page !== targetPage) {
        state.page = targetPage;
        render();
      }
      return setExpandedState(row, rowId, true, { forceReload });
    },
    collapseExpandedRow() {
      if (!state.expandedRowId.size) return false;
      const rowIds = Array.from(state.expandedRowId);
      let collapsed = false;
      rowIds.forEach(rowId => {
        const { row } = findRowById(rowId);
        if (!row) {
          state.expandedRowId.delete(rowId);
          state.loadingRowId.delete(rowId);
          collapsed = true;
        } else {
          if (setExpandedState(row, rowId, false)) {
            collapsed = true;
          }
        }
      });
      return collapsed;
    },
    getExpandedRowId() {
      return state.expandedRowId;
    },
    rerender: render,
    getState() {
      return { ...state };
    }
  };
}
